/**
 * Hoja Excel Facturación (MEJ-03b, PR6). Pura — sin Angular ni HTTP — para
 * poder probarla sin TestBed, igual que
 * `liquidaciones-agencia-excel-columnas.util.ts`.
 *
 * El componente (`liquidaciones-agencia.component.ts`, 4.600+ líneas) sólo
 * hace el wiring mínimo: botón + una llamada a `buildFacturacionWorkbook` y
 * la descarga del blob. Toda la forma de la hoja vive acá.
 */
import * as ExcelJS from 'exceljs';
import { ILiquidacionAgenciaFacturacionRow } from '@opb-workspace/shared';

export interface FacturacionColumnHeader {
    header: string;
    key: keyof ILiquidacionAgenciaFacturacionRow;
    width: number;
}

/**
 * Orden y rótulos EXACTOS de `specs/agency-billing-export/spec.md` y de la
 * solicitud de OPB (MEJ-03 Excel Facturacion.xlsx). "OS Venta Extra" es la
 * columna confirmada por OPB el 2026-09-30 (D10) para la O/S de venta sin
 * instructivo ni orden de transporte.
 */
export const FACTURACION_COLUMN_HEADERS: ReadonlyArray<FacturacionColumnHeader> =
    [
        { header: 'Nro. Factura', key: 'nroFactura', width: 15 },
        { header: 'Nro. Nota de Cobro', key: 'nroNotaCobro', width: 18 },
        { header: 'N° Liquidación', key: 'numeroLiquidacion', width: 15 },
        { header: 'Mes/Año', key: 'mesAnio', width: 12 },
        { header: 'Cliente', key: 'cliente', width: 25 },
        { header: 'Estado', key: 'estado', width: 12 },
        { header: 'Tipo', key: 'tipo', width: 12 },
        { header: 'CNTRS', key: 'cntr', width: 10 },
        { header: 'PLTS', key: 'plts', width: 10 },
        { header: 'Agenciamiento', key: 'agenciamiento', width: 15 },
        { header: 'Transporte', key: 'transporte', width: 15 },
        { header: 'Gate Out Venta', key: 'gateOutVenta', width: 15 },
        { header: 'Gate In Venta', key: 'gateInVenta', width: 15 },
        { header: 'OS Venta Transporte', key: 'osVentaTransporte', width: 18 },
        { header: 'OS Venta Agencia', key: 'osVentaAgencia', width: 16 },
        { header: 'OS Venta Extra', key: 'osVentaExtra', width: 15 },
        { header: 'Gestión OPB', key: 'gestionOpb', width: 14 },
        { header: 'Total Neto', key: 'totalNeto', width: 16 },
        {
            header: 'Total Traspaso Bruto',
            key: 'totalTraspasoBruto',
            width: 18,
        },
        { header: 'Total', key: 'total', width: 15 },
    ];

/** Claves de monto: formato de miles y alineación a la derecha, como en el Excel Cabecera. */
export const FACTURACION_NUMERIC_KEYS: ReadonlySet<string> = new Set([
    'cntr',
    'plts',
    'agenciamiento',
    'transporte',
    'gateOutVenta',
    'gateInVenta',
    'osVentaTransporte',
    'osVentaAgencia',
    'osVentaExtra',
    'gestionOpb',
    'totalNeto',
    'totalTraspasoBruto',
    'total',
]);

/**
 * Σ del desglose de facturación. Debe igualar `totalNeto` por construcción
 * (invariante del backend, `buildFacturacionBreakdown` /
 * `sumFacturacionBreakdown`) — replicada acá sólo para que el test del
 * Excel pruebe la MISMA invariante sobre las filas ya recibidas del backend,
 * sin volver a calcular nada de negocio.
 */
export function sumaDesgloseFacturacion(
    row: ILiquidacionAgenciaFacturacionRow
): number {
    return (
        row.agenciamiento +
        row.transporte +
        row.gateOutVenta +
        row.gateInVenta +
        row.osVentaTransporte +
        row.osVentaAgencia +
        row.osVentaExtra +
        row.gestionOpb
    );
}

const COLOR_HEADER_FILL = 'FF1E3A8A';
const COLOR_HEADER_FONT = 'FFFFFFFF';

/**
 * Arma el workbook completo de la hoja Excel Facturación, enlazando cada
 * celda por CLAVE de `FACTURACION_COLUMN_HEADERS` (lección #108: enlazar por
 * posición desincroniza la fila en silencio si el orden de columnas cambia).
 */
export function buildFacturacionWorkbook(
    rows: ReadonlyArray<ILiquidacionAgenciaFacturacionRow>
): ExcelJS.Workbook {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Excel Facturación');

    FACTURACION_COLUMN_HEADERS.forEach((col, index) => {
        worksheet.getColumn(index + 1).width = col.width;
    });

    const headerRow = worksheet.getRow(1);
    headerRow.height = 24;
    FACTURACION_COLUMN_HEADERS.forEach((col, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = col.header;
        cell.font = {
            name: 'Calibri',
            size: 12,
            bold: true,
            color: { argb: COLOR_HEADER_FONT },
        };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: COLOR_HEADER_FILL },
        };
        cell.alignment = {
            vertical: 'middle',
            horizontal: 'center',
            wrapText: true,
        };
    });

    rows.forEach((row, index) => {
        const excelRow = worksheet.getRow(index + 2);
        FACTURACION_COLUMN_HEADERS.forEach((col, colIndex) => {
            const cell = excelRow.getCell(colIndex + 1);
            const valor = row[col.key];
            cell.value = (valor as string | number | null | undefined) ?? '';
            if (FACTURACION_NUMERIC_KEYS.has(col.key)) {
                cell.numFmt = '#,##0';
                cell.alignment = { horizontal: 'right' };
            }
        });
    });

    return workbook;
}

export function buildFacturacionFilename(fecha: Date = new Date()): string {
    return `excel_facturacion_${fecha.toISOString().split('T')[0]}.xlsx`;
}
